import type { useTranslations } from 'next-intl';
import { toast } from '@/app/components/ui/use-toast';
import {
  AVAILABLE_MODEL_VERSIONS,
  DEFAULT_SAMPLE_COUNT,
  MODEL_CAPABILITIES,
  MODEL_VERSION,
  MODEL_VERSION_SETTINGS_MAP,
  SETTINGS_CONFIG,
  SETTINGS_PLAN_RESTRICTIONS,
  TASK_SETTING_OPTION,
  TASK_TRANSITION_PRO_AD,
} from '@/app/constants';
import type { FormTabType, ModelVersion, SiteType, SubsPlanType, TaskSettings, TemplateSettings } from '@/app/types';
import { isHevcSupported } from './device-utils';
import { isImageTask } from './task-types';

type NumericGroupData = { total: number; remaining: number; durations?: number[] };
type ScalarSettingKey = {
  [K in keyof TaskSettings]-?: NonNullable<TaskSettings[K]> extends string | number | boolean ? K : never;
}[keyof TaskSettings];
type ScalarSettingValue = NonNullable<TaskSettings[ScalarSettingKey]>;

const Q3_CHARACTER2VIDEO_PRO_AD_DURATION_OPTIONS = [
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
] as const;

export type GetAvailableOptionsContext = {
  transition?: string;
  resolution?: string;
  site?: SiteType;
  date?: Date;
};

export const isQ4HighResolution = (version: ModelVersion, resolution?: string) =>
  version === MODEL_VERSION.Q4 && (resolution === '2k' || resolution === '4k');

export const isQ3Q4NopeakAvailable = (
  version: ModelVersion,
  context?: Pick<GetAvailableOptionsContext, 'resolution' | 'site' | 'date'>,
) => !isQ4HighResolution(version, context?.resolution);

/**
 * 获取指定模型版本与表单类型的可用选项
 */
export const getAvailableOptions = (
  version: ModelVersion,
  type: Exclude<FormTabType, 'scene'>,
  context?: GetAvailableOptionsContext,
): Record<string, readonly string[]> => {
  const settingsKey = MODEL_VERSION_SETTINGS_MAP[version];
  if (!settingsKey) return {};
  const capability = MODEL_CAPABILITIES[settingsKey];
  if (!capability) return {};
  const base = capability.options;
  const override = capability.perTypeOverrides[type];
  const merged = override ? { ...base, ...override } : { ...base };
  const scheduledOptions =
    !isQ3Q4NopeakAvailable(version, context) && merged.schedule_mode
      ? { ...merged, schedule_mode: merged.schedule_mode.filter((value) => value !== 'nopeak') }
      : merged;

  if (version === MODEL_VERSION.Q3 && type === 'character2video' && context?.transition === TASK_TRANSITION_PRO_AD) {
    return {
      ...scheduledOptions,
      duration: Q3_CHARACTER2VIDEO_PRO_AD_DURATION_OPTIONS,
    };
  }

  return scheduledOptions;
};

export const getAvailableSettingsConfig = (
  version: ModelVersion,
  type: Exclude<FormTabType, 'scene'>,
  context?: GetAvailableOptionsContext,
) => {
  const settingsKey = MODEL_VERSION_SETTINGS_MAP[version];
  const config = SETTINGS_CONFIG[type][settingsKey] || [];
  return isQ3Q4NopeakAvailable(version, context)
    ? config
    : config.filter((option) => option !== TASK_SETTING_OPTION.NonPeakMode);
};

/**
 * 检查值是否为有效的自定义宽高比格式（"数字:数字"）
 */
const isValidCustomAspectRatio = (value: string): boolean => {
  const parts = value.split(':');
  if (parts.length !== 2) return false;
  const [wide, high] = parts.map(Number.parseFloat);
  return !Number.isNaN(wide) && !Number.isNaN(high) && wide > 0 && high > 0;
};

/**
 * 验证并修正设置项，确保所有设置值都在可用的选项范围内
 *
 * @param version - 模型版本（如 '2.0'）
 * @param newSettings - 需要验证的设置对象
 * @returns 修正后的设置对象
 */
export const validateAndCorrectSettings = <T extends TaskSettings>({
  version,
  newSettings,
  t,
  type,
}: {
  version: ModelVersion;
  newSettings: T;
  t?: ReturnType<typeof useTranslations<never>>;
  type: Exclude<FormTabType, 'scene'>;
}): T => {
  // 1. 获取该版本与类型可用的设置选项
  const transitionForCapabilities =
    version === MODEL_VERSION.Q3 && type === 'character2video'
      ? String((newSettings as { transition?: string }).transition ?? '')
      : undefined;
  const availableOptions = getAvailableOptions(version, type, {
    transition: transitionForCapabilities,
    resolution: String(newSettings.resolution ?? ''),
  });
  if (!availableOptions) {
    // 如果没有找到对应的设置选项，直接返回原设置
    return newSettings;
  }

  // 3. 创建修正后的设置对象
  const correctedSettings = { ...newSettings } as T;
  const writableScalarSettings = correctedSettings as Record<ScalarSettingKey, ScalarSettingValue | undefined>;

  if (isQ4HighResolution(version, correctedSettings.resolution)) {
    correctedSettings.schedule_mode = 'normal';
  }

  // 4. 遍历设置项，验证每个属性的值
  Object.keys(availableOptions).forEach((settingKey) => {
    const availableValues = availableOptions[settingKey];

    // 修复：如果该字段在新设置中缺失，补充默认值（原逻辑的 `in` 守卫会跳过缺失字段）
    if (!(settingKey in correctedSettings)) {
      const defaultValue = availableValues[0];
      const numericKeys = ['duration', 'sample_count'];
      const shouldBeNumber = numericKeys.includes(settingKey);

      writableScalarSettings[settingKey as ScalarSettingKey] =
        settingKey === 'duration' && version === MODEL_VERSION.Q2_PRO && defaultValue === 'auto'
          ? (-1 as ScalarSettingValue)
          : shouldBeNumber && defaultValue !== 'auto'
            ? (Number(defaultValue) as ScalarSettingValue)
            : (defaultValue as ScalarSettingValue);
      return;
    }

    // 如果新设置中包含这个属性
    const currentValue = correctedSettings[settingKey as keyof T];
    const currentValueStr = String(currentValue);

    // Q2Video 模型：duration 为 -1 时对应 'auto' 选项
    const isAutoValue = settingKey === 'duration' && version === MODEL_VERSION.Q2_PRO && currentValue === -1;
    const normalizedValueStr = isAutoValue ? 'auto' : currentValueStr;

    // 检查当前值是否在可用的选项中
    const isInAvailableValues = availableValues.includes(normalizedValueStr);

    // 对于 aspect_ratio 字段，如果 availableValues 包含 'custom'，则允许自定义格式的值
    const isCustomAllowed =
      settingKey === 'aspect_ratio' && availableValues.includes('custom') && isValidCustomAspectRatio(currentValueStr);

    if (!isInAvailableValues && !isCustomAllowed) {
      // 如果不在可用范围内且不是有效的自定义值，使用第一个选项作为默认值
      // 修复：对于数值字段，写入 number 而非 string，保持类型一致
      const defaultValue = availableValues[0];
      const numericKeys = ['duration', 'sample_count'];
      const shouldBeNumber = numericKeys.includes(settingKey);

      writableScalarSettings[settingKey as ScalarSettingKey] =
        settingKey === 'duration' && version === MODEL_VERSION.Q2_PRO && defaultValue === 'auto'
          ? (-1 as ScalarSettingValue)
          : shouldBeNumber && defaultValue !== 'auto'
            ? (Number(defaultValue) as ScalarSettingValue)
            : (defaultValue as ScalarSettingValue);
    }
    if (
      version === MODEL_VERSION['2.0'] &&
      correctedSettings?.duration === 8 &&
      correctedSettings?.resolution !== '720p'
    ) {
      t &&
        toast({
          message: t('create.createForm.settings.resolution.unSupportToast'),
        });
      correctedSettings.duration = 4;
    }
  });

  return correctedSettings;
};

/**
 * 检查当前用户的订阅计划是否允许使用特定设置选项的值
 * 该函数通过查询 SETTINGS_PLAN_RESTRICTIONS 配置表判断权限。
 * 如果某个设置值没有限制，或用户的计划包含在允许列表中，则返回 true，否则返回 false。
 *
 * @param settings - 设置选项名称，如 'duration', 'resolution', 'model' 等
 * @param value - 设置选项的值，如 '4', '720p', 'general' 等
 * @param userPlan - 用户的订阅计划类型，默认为 'free'
 * @param modelVersion - 可选的模型版本，如果提供则优先使用该版本的覆盖配置
 * @returns 如果用户可以使用该设置值则返回 true，否则返回 false
 *
 * @example
 * // 检查 free 用户是否可以使用 720p 分辨率
 * isOptionAllowed('resolution', '720p', 'free'); // 取决于配置，可能返回 false
 *
 * // 检查 free 用户是否可以使用 1080p 分辨率（Q2 版本）
 * isOptionAllowed('resolution', '1080p', 'free', MODEL_VERSION.Q2); // Q2 版本下免费用户可用，返回 true
 *
 * // 检查 pro 用户是否可以使用 10 秒视频
 * isOptionAllowed('duration', '10', 'pro'); // 取决于配置，可能返回 true
 */
export const isOptionAllowed = (
  settings: keyof typeof SETTINGS_PLAN_RESTRICTIONS,
  value: string,
  userPlan = 'free',
  modelVersion?: ModelVersion,
  isNewMembership = false,
) => {
  const restriction = SETTINGS_PLAN_RESTRICTIONS[settings];
  if (!restriction) return true;

  // 优先使用 modelVersion 的覆盖配置
  let allowedPlans: SubsPlanType[] | undefined;
  if (modelVersion && restriction.perVersionOverrides?.[modelVersion]) {
    allowedPlans = restriction.perVersionOverrides[modelVersion]?.[value];
  }

  // 如果没有找到覆盖配置，使用基础配置
  if (allowedPlans === undefined) {
    allowedPlans =
      (isNewMembership ? restriction.membershipRevisionOptions?.[value] : undefined) ?? restriction.options[value];
  }

  const isDirectlyAllowed = allowedPlans?.includes(userPlan as SubsPlanType);

  // 如果 allowedPlans 为空数组，表示所有用户都可以使用（包括免费用户）
  // 如果 allowedPlans 为 undefined，表示没有限制
  // 如果 allowedPlans 有值，检查用户计划是否在允许列表中
  return allowedPlans === undefined || allowedPlans.length === 0 || isDirectlyAllowed;
};

/**
 * 检查指定表单类型是否支持指定的模型版本
 *
 * @param type - 表单类型
 * @param version - 模型版本
 * @returns 如果该表单类型支持该模型版本则返回 true，否则返回 false
 */
export const isModelVersionAvailable = (type: Exclude<FormTabType, 'scene'>, version: ModelVersion): boolean => {
  const availableVersions = AVAILABLE_MODEL_VERSIONS[type];
  return availableVersions.includes(version);
};

/**
 * 校验并修正任务设置，确保其符合当前用户的订阅计划和权限。
 * 如果某项设置不符合用户权限（例如选择了付费计划才有的分辨率），则将其重置为默认值。
 */
export const validateUserPermissionSettings = ({
  tabType,
  settings,
  modelVersion,
  userSubsPlan,
  isNewMembership = false,
  trials720p,
  trialsQ2TransitionPro,
  defaultSettings,
}: {
  tabType: Exclude<FormTabType, 'scene'>;
  settings: TaskSettings;
  modelVersion: ModelVersion;
  userSubsPlan?: string;
  isNewMembership?: boolean;
  trials720p?: NumericGroupData;
  trialsQ2TransitionPro?: NumericGroupData;
  defaultSettings: TaskSettings;
}) => {
  const newSettings = { ...settings };
  let hasInvalidSetting = false;

  // 生图任务免费用户所有选项都可用，不需要校验
  if (isImageTask({ type: tabType })) {
    return {
      settings: newSettings,
      hasInvalidSetting,
    };
  }

  // 检查设置是否对当前用户可用，否则应用默认值
  if (userSubsPlan) {
    // 检查 duration(视频时长) 是否对当前用户可用，否则重置为默认值
    if (
      newSettings.duration &&
      !isOptionAllowed('duration', newSettings.duration?.toString() || '', userSubsPlan, modelVersion)
    ) {
      newSettings.duration = defaultSettings.duration;
      hasInvalidSetting = true;
    }

    // 检查 transition 权限（只有 Q2 图生需要检查，Q3 都是免费的，不检查也行）
    if ('transition' in newSettings) {
      const shouldCheckTransition =
        modelVersion === MODEL_VERSION.Q2 && !['character2video', 'text2video'].includes(tabType);

      if (
        shouldCheckTransition &&
        !isOptionAllowed('transition', newSettings.transition || '', userSubsPlan, modelVersion) &&
        // Q2 免费次数未用完时，pro 模式可以 0 积分试用
        !(
          modelVersion === MODEL_VERSION.Q2 &&
          newSettings.transition === 'pro' &&
          trialsQ2TransitionPro?.remaining &&
          trialsQ2TransitionPro.remaining > 0
        )
      ) {
        newSettings.transition = defaultSettings.transition;
        hasInvalidSetting = true;
      }
    }

    // 检查 resolution 权限
    if (
      modelVersion !== MODEL_VERSION.Q1 && // Q1 1080p 不检查
      newSettings.resolution &&
      !isOptionAllowed('resolution', newSettings.resolution, userSubsPlan, modelVersion)
    ) {
      const has720pFreeAccess = trials720p?.remaining && newSettings.resolution === '720p';
      if (!has720pFreeAccess) {
        newSettings.resolution = defaultSettings.resolution;
        hasInvalidSetting = true;
      }
    }

    // 检查 sample_count(样本数量) 是否可用，否则重置为默认值
    if (
      newSettings.sample_count &&
      !isOptionAllowed('sample_count', newSettings.sample_count.toString(), userSubsPlan, modelVersion)
    ) {
      newSettings.sample_count = defaultSettings.sample_count;
      hasInvalidSetting = true;
    }

    if (
      newSettings.schedule_mode &&
      !isOptionAllowed('schedule_mode', newSettings.schedule_mode, userSubsPlan, modelVersion, isNewMembership)
    ) {
      newSettings.schedule_mode = defaultSettings.schedule_mode;
      hasInvalidSetting = true;
    }

    if (newSettings.codec) {
      if (!isOptionAllowed('codec', newSettings.codec || '', userSubsPlan)) {
        newSettings.codec = defaultSettings.codec;
        hasInvalidSetting = true;
      }

      // 如果浏览器不支持 H265 但是选择了 h265，强制切换到 H264，此过程对用户无感
      if (!isHevcSupported() && newSettings.codec?.startsWith('h265')) {
        newSettings.codec = 'h264';
      }
    }
  }

  return {
    settings: newSettings,
    hasInvalidSetting,
  };
};

/**
 * 校验并修正模板设置，确保其符合当前用户的订阅计划和权限。
 */
export const validateUserPermissionTemplateSettings = ({
  settings,
  userSubsPlan,
  defaultSampleCount = DEFAULT_SAMPLE_COUNT,
}: {
  settings: TemplateSettings;
  userSubsPlan?: string;
  defaultSampleCount?: number;
}) => {
  const newSettings = { ...settings };
  let hasInvalidSetting = false;

  // 检查设置是否对当前用户可用，否则应用默认值
  if (userSubsPlan) {
    // 检查 sample_count（样本数量）是否可用，否则重置为默认值
    if (
      newSettings.sample_count &&
      !isOptionAllowed('sample_count', newSettings.sample_count.toString(), userSubsPlan)
    ) {
      newSettings.sample_count = defaultSampleCount;
      hasInvalidSetting = true;
    }
  }

  return {
    settings: newSettings,
    hasInvalidSetting,
  };
};
